import { useBuilderWidget } from 'components/TasksV2/TaskCreation/contexts/BuilderWidgetContext'
import { BuilderSubtaskSettings } from 'components/TasksV2/TaskCreation/components/BuilderSubtaskSettings'
import WidgetHeader from 'components/TasksV2/UI/WidgetHeader'
import { WidgetWrapper } from 'components/TasksV2/UI/WidgetWrapper'
import React, { memo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Box } from '@chakra-ui/core'
import { PseudoBox } from '@chakra-ui/core'
import { ImageQeustion } from './ImageQeustion'
import { useWidgetErrorMessage } from '../../useWidgetErrorMessage'
import { WidgetProps } from '../types'
import { WidgetIsRequiredSwitch } from '../WidgetIsRequiredSwitch'
import { ImageDescription } from './ImageDescription'
import { ImageMediaGallery } from './ImageMediaGallery'
import { useVmAiEnhance } from 'components/TasksV2/TaskCreation/hooks/useVmAiEnhance'
import { useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { AiEnhanceBanner } from 'components/TasksV2/UI/AiEnhanceBanner'
import useFeatureFlag from 'hooks/useFeatureFlag'
import { FEATURE_FLAGS } from 'constants/featureFlags'
import colors from 'constants/colors'
import keys from 'constants/keys'

const ImageWidget: React.FC<WidgetProps> = memo(({ index, isDragging, field, remove, subtaskPath }) => {
  const builder = useBuilderWidget()
  const { control, errors, setValue, getValues, watch } = useFormContext()
  const { isVmTask, isEdit, isMediaUploading } = useFormStepContext()
  const { openForSubtask } = useVmAiEnhance()

  const questionName = `${subtaskPath}.title`

  const imageError = useWidgetErrorMessage({ errors, index, field })

  // The AI sub-flow is only offered while creating a VM task: existing subtasks cannot be updated
  // (editTask does not send sub_tasks), and AI review is a VM concept. Retailers without the `ai_vm`
  // flag see the plain photo subtask, with no trace of AI.
  const isAiVmEnabled = useFeatureFlag(FEATURE_FLAGS.AI_VM)

  // Dual View creates AI cards through its dedicated menu and owns their local workflow.
  const showAiControls = !builder && isVmTask && !isEdit && isAiVmEnabled
  // The icon follows the flag too, so the card cannot advertise AI while offering no way to turn it off.
  const isAiEnhanced = !!watch(`${subtaskPath}.is_ai_enhanced`) && isAiVmEnabled

  // Snapshot the whole step-1 form: the AI steps replace it in the modal, so it has to be persisted
  // before navigating (see useVmAiEnhance).
  const handleOpenAiFlow = () => {
    const values = getValues({ nest: true }) as { title?: string; sub_tasks?: any[] }

    openForSubtask(index, values)
  }

  return (
    <WidgetWrapper pos="relative" p="10px" isError={!!imageError} errorMessage={imageError} isDragging={isDragging}>
      {/* `isAiEnhanced` comes from watch(), so the icon follows the banner's state immediately */}
      <WidgetHeader onRemove={remove} subtaskType={field.type} isAiEnhanced={isAiEnhanced} />

      <ImageQeustion questionName={questionName} field={field} />

      {!builder && <WidgetIsRequiredSwitch subtaskPath={subtaskPath} />}

      {/* The single AI affordance — always present, both states. Breathing room before the description
          card below: the banner must not sit flush against the textarea. */}
      {showAiControls && (
        <Box mt="10px" mb="14px">
          <AiEnhanceBanner
            isAiEnhanced={isAiEnhanced}
            isDisabled={isMediaUploading}
            onGenerate={handleOpenAiFlow}
            onDisable={() => setValue(`${subtaskPath}.is_ai_enhanced`, false)}
          />
        </Box>
      )}

      <PseudoBox
        className={builder ? 'creation-media-fields' : undefined}
        border="1px solid"
        borderColor={colors.greyBorder}
        borderRadius="8px"
        _focus={{ borderColor: '#f5f5f5', boxShadow: '0 0 0 1px #f5f5f5' }}
      >
        {/* Description Component - always visible */}
        <ImageDescription subtaskPath={subtaskPath} />

        {/* Media Gallery Component */}
        <ImageMediaGallery subtaskPath={subtaskPath} enableEdit={true} />
      </PseudoBox>
      {builder && <BuilderSubtaskSettings subtaskPath={subtaskPath} defaultCategory={field.category} />}

      {/* Register constant values in the form context */}
      <Controller
        name={`${subtaskPath}.type`}
        control={control}
        as={<Box />}
        key={`${subtaskPath}.type`}
        defaultValue={field.type}
      />

      {/* Hidden Controllers for AI fields — unregistered fields are dropped from handleSubmit data.
          `is_ai_enhanced` has no visible control any more (the switch is gone), so it is registered
          here unconditionally; the banner only ever reads it and writes via setValue. */}
      <Controller
        name={`${subtaskPath}.is_ai_enhanced`}
        control={control}
        as={<Box />}
        key={`${subtaskPath}.is_ai_enhanced`}
        defaultValue={(field as any).is_ai_enhanced ?? false}
      />
      <Controller
        name={`${subtaskPath}.prompt_id`}
        control={control}
        as={<Box />}
        key={`${subtaskPath}.prompt_id`}
        defaultValue={keys.AI_IMAGE_SUBTASK.CONST_PROMPT_ID}
      />
      <Controller
        name={`${subtaskPath}.message_id`}
        control={control}
        as={<Box />}
        key={`${subtaskPath}.message_id`}
        defaultValue={getValues(`${subtaskPath}.message_id`) || ''}
      />
      <Controller
        name={`${subtaskPath}.reference_images`}
        control={control}
        as={<Box />}
        key={`${subtaskPath}.reference_images`}
        defaultValue={(field as any).reference_images ?? []}
      />
      <Controller
        name={`${subtaskPath}.ai_prompt`}
        control={control}
        as={<Box />}
        key={`${subtaskPath}.ai_prompt`}
        defaultValue={(field as any).ai_prompt ?? ''}
      />
    </WidgetWrapper>
  )
})

export default ImageWidget
